import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  TSubscriptionLifeCycleStatus,
  TUserKnowledgeTestSelectKey,
  TUserKycSelectKey,
  TUserSuitabilityTestSelectKey,
  TUserVerificationDetailsSelectKey
} from '@/lib/supabase/supabase.interface'
import { IUserVerificationDetailsState } from './user-verification-details.interface'
import { AppState } from '..'
import { createSelector } from 'reselect'

const initialState: IUserVerificationDetailsState = {
  userVerificationDetails: {},
  userVerificationDetailsSubLifeCycleStatus: 'unknown',
  userSuitabilityTest: {},
  userSuitabilityTestSubLifeCycleStatus: 'unknown',
  userKnowledgeTest: {},
  userKnowledgeTestSubLifeCycleStatus: 'unknown',
  userKyc: {},
  userKycSubLifeCycleStatus: 'unknown'
}

const userVerificationDetailsSlice = createSlice({
  name: 'userVerificationDetails',
  initialState,
  reducers: {
    setUserVerificationDetails: (
      state,
      action: PayloadAction<TUserVerificationDetailsSelectKey>
    ) => {
      state.userVerificationDetails = action.payload
    },
    setUserVerificationDetailsSubLifeCycleStatus: (
      state,
      action: PayloadAction<TSubscriptionLifeCycleStatus>
    ) => {
      state.userVerificationDetailsSubLifeCycleStatus = action.payload
    },
    setUserSuitabilityTest: (
      state,
      action: PayloadAction<TUserSuitabilityTestSelectKey>
    ) => {
      state.userSuitabilityTest = action.payload
    },
    setUserSuitabilityTestSubLifeCycleStatus: (
      state,
      action: PayloadAction<TSubscriptionLifeCycleStatus>
    ) => {
      state.userSuitabilityTestSubLifeCycleStatus = action.payload
    },
    setUserKnowledgeTest: (
      state,
      action: PayloadAction<TUserKnowledgeTestSelectKey>
    ) => {
      state.userKnowledgeTest = action.payload
    },
    setUserKnowledgeTestSubLifeCycleStatus: (
      state,
      action: PayloadAction<TSubscriptionLifeCycleStatus>
    ) => {
      state.userKnowledgeTestSubLifeCycleStatus = action.payload
    },
    setUserKyc: (state, action: PayloadAction<TUserKycSelectKey>) => {
      state.userKyc = action.payload
    },
    setUserKycSubLifeCycleStatus: (
      state,
      action: PayloadAction<TSubscriptionLifeCycleStatus>
    ) => {
      state.userKycSubLifeCycleStatus = action.payload
    },
    clearUserVerificationDetails: (state) => {
      state.userVerificationDetails = {} as Record<string, unknown>
    },
    clearUserSuitabilityTest: (state) => {
      state.userSuitabilityTest = {} as Record<string, unknown>
    },
    clearUserKnowledgeTest: (state) => {
      state.userKnowledgeTest = {} as Record<string, unknown>
    },
    clearUserKyc: (state) => {
      state.userKyc = {} as Record<string, unknown>
    }
  }
})

const getUserVerificationDetails = (state: AppState) =>
  state.userVerificationDetails.userVerificationDetails
const getUserVerificationDetailsSubLifeCycleStatus = (state: AppState) =>
  state.userVerificationDetails.userVerificationDetailsSubLifeCycleStatus

const getUserSuitabilityTest = (state: AppState) =>
  state.userVerificationDetails.userSuitabilityTest
const getUserSuitabilityTestSubLifeCycleStatus = (state: AppState) =>
  state.userVerificationDetails.userSuitabilityTestSubLifeCycleStatus

const getUserKnowledgeTest = (state: AppState) =>
  state.userVerificationDetails.userKnowledgeTest
const getUserKnowledgeTestSubLifeCycleStatus = (state: AppState) =>
  state.userVerificationDetails.userKnowledgeTestSubLifeCycleStatus

const getUserKycSubLifeCycleStatus = (state: AppState) =>
  state.userVerificationDetails.userKycSubLifeCycleStatus
export const getUserKyc = (state: AppState) =>
  state.userVerificationDetails.userKyc

export const userVerificationDetailsSelector = createSelector(
  [getUserVerificationDetails, getUserVerificationDetailsSubLifeCycleStatus],
  (userVerificationDetails, userVerificationDetailsSubLifeCycleStatus) => ({
    userVerificationDetails,
    userVerificationDetailsSubLifeCycleStatus
  })
)

export const userVerificationStatusAndSuitTestSelector = createSelector(
  [
    getUserVerificationDetailsSubLifeCycleStatus,
    getUserSuitabilityTest,
    getUserSuitabilityTestSubLifeCycleStatus
  ],
  (
    userVerificationDetailsSubLifeCycleStatus,
    userSuitabilityTest,
    userSuitabilityTestSubLifeCycleStatus
  ) => ({
    userVerificationDetailsSubLifeCycleStatus,
    userSuitabilityTest,
    userSuitabilityTestSubLifeCycleStatus
  })
)

export const userVerificationStatusAndKnowledgeTestSelector = createSelector(
  [
    getUserVerificationDetailsSubLifeCycleStatus,
    getUserKnowledgeTest,
    getUserKnowledgeTestSubLifeCycleStatus
  ],
  (
    userVerificationDetailsSubLifeCycleStatus,
    userKnowledgeTest,
    userKnowledgeTestSubLifeCycleStatus
  ) => ({
    userVerificationDetailsSubLifeCycleStatus,
    userKnowledgeTest,
    userKnowledgeTestSubLifeCycleStatus
  })
)

export const userVerificationStatusAndKycSelector = createSelector(
  [
    getUserVerificationDetailsSubLifeCycleStatus,
    getUserKyc,
    getUserKycSubLifeCycleStatus
  ],
  (
    userVerificationDetailsSubLifeCycleStatus,
    userKyc,
    userKycSubLifeCycleStatus
  ) => ({
    userVerificationDetailsSubLifeCycleStatus,
    userKyc,
    userKycSubLifeCycleStatus
  })
)

export const userSuitTestSelector = createSelector(
  [getUserSuitabilityTest, getUserSuitabilityTestSubLifeCycleStatus],
  (userSuitabilityTest, userSuitabilityTestSubLifeCycleStatus) => ({
    userSuitabilityTest,
    userSuitabilityTestSubLifeCycleStatus
  })
)

export const userKnowledgeSelector = createSelector(
  [getUserKnowledgeTest, getUserKnowledgeTestSubLifeCycleStatus],
  (userKnowledgeTest, userKnowledgeTestSubLifeCycleStatus) => ({
    userKnowledgeTest,
    userKnowledgeTestSubLifeCycleStatus
  })
)

export const {
  setUserVerificationDetails,
  setUserVerificationDetailsSubLifeCycleStatus,
  setUserSuitabilityTest,
  setUserSuitabilityTestSubLifeCycleStatus,
  setUserKnowledgeTest,
  setUserKnowledgeTestSubLifeCycleStatus,
  setUserKyc,
  setUserKycSubLifeCycleStatus,
  clearUserVerificationDetails,
  clearUserSuitabilityTest,
  clearUserKnowledgeTest,
  clearUserKyc
} = userVerificationDetailsSlice.actions
export default userVerificationDetailsSlice.reducer
